import {
  useDebounceCallback,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  PriceSectionEditable,
  ProductCard,
} from '@airship-platform/airship-proposal';
import {
  deriveItemGroups,
  isItemGroupRoot,
} from '@airship-platform/appointments-feature/utils';
import {
  FlexBox,
  NumberInput,
  SectionCard,
  SectionSkeleton,
} from '@airship-platform/jam-web/components';
import {
  LinkIcon,
  MaterialIcon,
  ReorderIcon,
  SystemIcon,
} from '@airship-platform/jam-web/icons';
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable,
} from '@hello-pangea/dnd';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { useMemo } from 'react';
import { useAppointmentProposalOptionModifiers } from '../../data-access/appointmentProposalOptionModifiers';
import {
  AppointmentProposalOptionProduct,
  useAppointmentProposalOptionProducts,
} from '../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import { OfflineSyncErrorChip } from '../../patterns';
import { useItemGroupMutations } from '../proposal-item-groups';
import {
  RemoveProductModal,
  useProductDragAndDrop,
  useProductNavigationActions,
} from '../proposal-option-items';
import { useOptionCard } from '../proposal-options-buider/hooks/useOptionCard';
import AddProductsButton, {
  BUTTON_CONFIG,
} from './components/AddProductsButton';

type ProductGroupingName = 'SYSTEM' | 'ADDONS' | 'UPGRADES';

interface OptionProductsProps {
  groupingName: ProductGroupingName;
  showAddButton?: boolean;
}

interface SectionProps {
  title: string;
  icon: (size: number) => React.ReactNode;
}

const SECTION_CONFIG: Record<ProductGroupingName, SectionProps> = {
  SYSTEM: {
    title: 'Services and equipment',
    icon: (size) => <SystemIcon width={size} height={size} />,
  },
  ADDONS: {
    title: 'Materials',
    icon: (size) => <MaterialIcon width={size} height={size} />,
  },
  UPGRADES: {
    title: 'Upgrades',
    icon: (size) => <SystemIcon width={size} height={size} />,
  },
};

const OptionProducts: React.FC<OptionProductsProps> = ({
  groupingName,
  showAddButton = true,
}) => {
  const { currentOption, currentOptionCosts } = useAppointmentProposalOptions();
  const { proposalModifiers } = useAppointmentProposalOptionModifiers();
  const {
    proposalProducts,
    isLoading,
    isLoaded,
    updateAppointmentProposalOptionProduct,
  } = useAppointmentProposalOptionProducts();
  const { deleteProductCascading } = useItemGroupMutations();
  const { handleDragEnd } = useProductDragAndDrop();
  const { navigateToQueryProducts } = useProductNavigationActions();
  const { state, removeProduct, showRemoveProduct } = useOptionCard();
  const { showToast } = useToast();

  const section = SECTION_CONFIG[groupingName];
  const isReadOnly = currentOption?.isReadOnly || false;

  const { displayProducts, itemGroupsByRootId } = useMemo(
    () =>
      deriveItemGroups(
        currentOption
          ? proposalProducts.filter(
              (p) =>
                p.appointmentProposalOptionId ===
                currentOption.appointmentProposalOptionId
            )
          : []
      ),
    [currentOption, proposalProducts]
  );

  const products = useMemo(
    () =>
      displayProducts
        .filter((p) => p.groupingName === groupingName)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [displayProducts, groupingName]
  );

  const hasItems = !!products.length;

  const modifierQualifyingIds = useMemo(
    () =>
      new Set(
        (currentOptionCosts?.appliedRebates || []).flatMap((d) =>
          proposalModifiers
            .filter(
              (modifier) =>
                modifier.discountsAndFeesId === d.discountsAndFeesId &&
                currentOption?.appointmentProposalOptionId ===
                  modifier.appointmentProposalOptionId
            )
            .flatMap((modifier) => [
              ...modifier.qualifyingPricebookIds,
              ...modifier.qualifyingPricebookCategoryIds,
            ])
        )
      ),
    [
      currentOptionCosts?.appliedRebates,
      currentOption?.appointmentProposalOptionId,
      proposalModifiers,
    ]
  );

  const handleQuantityChange = useDebounceCallback(
    (appointmentProposalOptionProductId: string, newQuantity: number) => {
      if (!currentOption) return;
      const {
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
      } = currentOption;
      updateAppointmentProposalOptionProduct(
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        appointmentProposalOptionProductId,
        { quantity: newQuantity }
      );
    },
    1000
  );

  const handleProductDelete = (product: AppointmentProposalOptionProduct) => {
    deleteProductCascading(product).then(() => {
      showToast('Product removed', 'success');
      removeProduct();
    });
  };

  const renderProductItem = (
    product: AppointmentProposalOptionProduct,
    dragHandleProps?: DraggableProvidedDragHandleProps | null
  ) => {
    const itemGroup = itemGroupsByRootId.get(
      product.appointmentProposalOptionProductId
    );
    const subItemCount = itemGroup?.children.length ?? 0;
    const isParentGroupRoot = isItemGroupRoot(product);

    return (
      <FlexBox
        flexDirection={['column', 'row']}
        alignItems={'center'}
        justifyContent="space-between"
        gap={3}
        py={2}
      >
        <ProductCard
          product={{
            ...product,
            name: product.itemGroupName || product.name,
          }}
          showDescription={false}
          productNameSuffix={
            <>
              <OfflineSyncErrorChip errors={product.offlineSyncErrors} />
              {isParentGroupRoot && (
                <Chip
                  color="softBlue"
                  label={`${subItemCount} sub-item${subItemCount === 1 ? '' : 's'}`}
                  size="small"
                />
              )}
              {!!product.linkedItems.length && (
                <Chip
                  color="softGray"
                  icon={<LinkIcon width={14} height={14} />}
                  label={`${product.linkedItems.length} linked`}
                  size="small"
                />
              )}
              {[product.pricebookId, ...product.categoryIds].some((id) =>
                modifierQualifyingIds.has(id)
              ) ? (
                <Chip color="softBlue" label="Rebate available" size="small" />
              ) : null}
              {!!product.durationBillingId && (
                <Chip color="softGreen" label="Membership" size="small" />
              )}
            </>
          }
          priceSection={
            <PriceSectionEditable
              showFullPrice={true}
              quantity={itemGroup ? 1 : product.quantity}
              defaultPriceType="STANDARD"
              prices={[
                {
                  currency: product.currency,
                  value: itemGroup?.total ?? product.price,
                  priceType: 'STANDARD',
                  taxable: product.taxable,
                  hasDiscount:
                    itemGroup?.hasMembership || product.hasMembership,
                  valueWithDiscount:
                    itemGroup?.membershipTotal ?? product.membershipPrice ?? 0,
                },
              ]}
            />
          }
          productClickRoute={`product/${product.appointmentProposalOptionProductId}`}
        />
        <FlexBox
          flexDirection={'column'}
          alignItems={'flex-end'}
          justifyContent={'flex-end'}
          gap={2}
        >
          <FlexBox alignItems={'center'} gap={2}>
            {!isParentGroupRoot && (
              <NumberInput
                initialValue={product.quantity || 1}
                disabled={isReadOnly}
                onChange={(newQuantity: number) =>
                  handleQuantityChange(
                    product.appointmentProposalOptionProductId,
                    newQuantity
                  )
                }
                onDelete={() =>
                  showRemoveProduct(product.appointmentProposalOptionProductId)
                }
              />
            )}
            <FlexBox flexDirection={'column'} gap={2} alignItems={'flex-end'}>
              <IconButton
                size="small"
                disabled={isReadOnly}
                sx={{ cursor: isReadOnly ? 'default' : 'grab' }}
                {...dragHandleProps}
              >
                <ReorderIcon />
              </IconButton>
            </FlexBox>
          </FlexBox>
          {!isReadOnly && !isParentGroupRoot && !product.durationBillingId && (
            <FlexBox alignSelf={'flex-end'} alignItems={'flex-end'}>
              <Link
                variant={'caption'}
                color={'textSecondary'}
                onClick={() => {
                  const config = BUTTON_CONFIG[groupingName];
                  navigateToQueryProducts({
                    productTypes: config.productTypes,
                    groupingName,
                    productPageTitle: config.replacePageTitle,
                    replaceProductId:
                      product.appointmentProposalOptionProductId,
                    categoryIds: product.categoryIds,
                  });
                }}
              >
                Replace
              </Link>
            </FlexBox>
          )}
        </FlexBox>
      </FlexBox>
    );
  };

  const renderRemovalModal = () => {
    const product = proposalProducts.find(
      (p) => p.appointmentProposalOptionProductId === state.currentProductId
    );
    return (
      product && (
        <RemoveProductModal
          isModalOpen={state.isRemoveProductModalOpen}
          onModalClose={() => showRemoveProduct(null)}
          isLoading={isLoading}
          onProductRemove={() => handleProductDelete(product)}
          product={product}
        />
      )
    );
  };

  if (
    !currentOption ||
    (groupingName === 'UPGRADES' && !isLoading && !hasItems)
  ) {
    return null;
  }

  if (isLoading && !isLoaded) {
    return <SectionSkeleton />;
  }

  return (
    <FlexBox flexDirection="column" gap={2}>
      <SectionCard
        title={
          <FlexBox gap={2} alignItems="center">
            {section.title}
            {groupingName === 'SYSTEM' &&
              !!currentOption.certifications.length && (
                <Chip color="gold" label="AHRI match" />
              )}
          </FlexBox>
        }
        icon={section.icon(hasItems ? 70 : 100)}
        hasItems={hasItems}
        onClick={
          !isReadOnly
            ? () => {
                const config = BUTTON_CONFIG[groupingName];
                navigateToQueryProducts({
                  productTypes: config.productTypes,
                  groupingName,
                  productPageTitle: config.productPageTitle,
                });
              }
            : undefined
        }
        action={
          showAddButton && (
            <AddProductsButton
              groupingName={groupingName}
              variant={hasItems ? 'button' : 'iconButton'}
            />
          )
        }
      >
        <DragDropContext
          onDragEnd={(result) => handleDragEnd(result, products)}
        >
          <Droppable droppableId={`${groupingName}-products`}>
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                {products.map((product, index) => (
                  <Draggable
                    key={product.appointmentProposalOptionProductId}
                    draggableId={product.appointmentProposalOptionProductId}
                    index={index}
                    isDragDisabled={isReadOnly}
                  >
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.draggableProps}>
                        {renderProductItem(product, provided.dragHandleProps)}
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </SectionCard>
      {renderRemovalModal()}
    </FlexBox>
  );
};

export default OptionProducts;
